// src/components/Signup.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import "./sign-up.css";
import logo from "../components/Images/KostPadi-Icon.png";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [compName, setCompName] = useState("");
  const [phone, setPhone] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return Math.min(score, 4);
  }, [password]);

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            company_name: compName.trim(),
            phone: phone.trim(),
          },
        },
      });

      if (error) {
        setErrorMessage(error.message);
        return;
      }

      if (!data?.user) {
        setErrorMessage("Unexpected error: no user returned.");
        return;
      }

      setSuccessMessage(
        "Account created successfully. Please log in to continue."
      );

      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      console.error("Signup error:", err);
      setErrorMessage(
        err?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout signup-layout">
      <aside className="auth-aside">
        <div className="brand-head">
          <img src={logo} alt="KostPadi" />
          <h1>KostPadi</h1>
        </div>

        <p className="tagline">
          A modern SME finance suite—estimate costs, invoice faster, and stay on
          top of cash flow.
        </p>

        <ul className="pill-list">
          <li>VAT/WHT-aware</li>
          <li>Estimate → Invoice</li>
          <li>Supabase RLS</li>
          <li>Export CSV/PDF</li>
        </ul>

        <div className="aside-footer">
          <a href="/" className="aside-link">
            ← Back to site
          </a>
        </div>
      </aside>

      <main className="auth-main">
        <div className="signup-card">
          <header className="signup-head">
            <img src={logo} alt="KostPadi Logo" className="logo-img" />
            <div>
              <h2>Create your account</h2>
              <p className="muted">Start free. No credit card required.</p>
            </div>
          </header>

          {errorMessage && (
            <div className="error-banner" role="alert">
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div className="success-banner" role="status">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleEmailSignup} className="signup-form">
            <div className="grid">
              <label>
                First Name
                <input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>

              <label>
                Last Name
                <input
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>
            </div>

            <label>
              Company Name
              <input
                type="text"
                placeholder="Company Name"
                value={compName}
                onChange={(e) => setCompName(e.target.value)}
                required
                disabled={loading}
              />
            </label>

            <div className="grid">
              <label>
                Phone Number
                <input
                  type="tel"
                  placeholder="+234…"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>
            </div>

            <label>
              Password
              <div className="pw-wrap">
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />

                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((s) => !s)}
                  disabled={loading}
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <div className={`pw-meter s${strength}`}>
                <span />
                <span />
                <span />
                <span />
                <em className="pw-label">
                  {["Too weak", "Weak", "Okay", "Good", "Strong"][strength]}
                </em>
              </div>
            </label>

            <button type="submit" id="signup-btn" disabled={loading}>
              {loading ? "Creating account…" : "Sign Up"}
            </button>

            <p className="small login-link">
              Already have an account? <a href="/login">Log in</a>
            </p>

            <p className="tiny-note">
              By signing up you agree to our <a href="/">Terms</a> and{" "}
              <a href="/">Privacy Policy</a>.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Signup;
